/**
 * Página de Organización - Gestión jerárquica
 * - Unión: Gestiona asociaciones y ve todos los clubes
 * - Asociación: Gestiona los clubes de su asociación
 * - Director de Club: Solo ve su club
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  InlineLoader,
  SectionBadge,
} from "@/components/ui";
import {
  Plus,
  ChevronRight,
  Eye,
  Building2,
  Users,
  Shield,
  AlertCircle,
  CheckCircle,
  Church,
  Search,
  Filter,
} from "lucide-react";
import {
  useUnions,
  useAssociationsByUnion,
  useAssociations,
  useChurchesByAssociation,
} from "@/hooks/useOrganization";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/authStore";
import { useRoleAssignments } from "@/hooks/useRoles";
import { useScopedMembers } from "@/hooks/usePersons";
import AssociationModal from "./components/AssociationModal";
import CreateClubModal from "./components/CreateClubModal";
import type { Union, Association, Club, ClubType } from "@/types";
import { getActiveSections } from "@/lib/utils";
import { sortSections } from "@/lib/constants/sections";

type ModalType = "association" | "church" | "club" | null;

export default function OrganizationPage() {
  const { isClubDirector, isAssociationAdmin } = usePermissions();

  // Director de club: vista simplificada
  if (isClubDirector) {
    return <ClubDirectorOrganizationView />;
  }

  // Asociación: gestión de sus clubes
  if (isAssociationAdmin) {
    return <AssociationOrganizationView />;
  }

  // Unión: vista completa (asociaciones + clubes)
  return <AdminOrganizationView />;
}

/**
 * Vista simplificada para directores de club
 * Solo muestra información de su club
 */
function ClubDirectorOrganizationView() {
  const navigate = useNavigate();
  const { profile } = useAuthStore();

  // Director club view: use sections
  const directorClub = profile?.directorOfClub;
  const directorSections = directorClub ? (directorClub as any).sections : null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          Mi Club
        </h1>
        <p className="text-gray-500 mt-1 ml-[52px]">Información de tu club</p>
      </div>

      {/* Info del Club */}
      {directorClub ? (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="flex flex-row items-center gap-4 bg-gradient-to-r from-gray-50/50 to-primary-50/30">
            {directorClub.logoUrl ? (
              <img
                src={directorClub.logoUrl}
                alt={`Logo de ${directorClub.name}`}
                className="w-14 h-14 rounded-xl object-cover shadow-lg ring-2 ring-white"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/30">
                <Building2 className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <CardTitle className="text-xl">{directorClub.name}</CardTitle>
              <div className="flex gap-1 mt-1">
                {directorSections ? (
                  sortSections(
                    Object.entries(directorSections)
                      .filter(([, cfg]: [string, any]) => cfg?.active)
                      .map(([key]) => key),
                  ).map((key) => (
                    <SectionBadge
                      key={key}
                      section={key as ClubType}
                      size="xs"
                    />
                  ))
                ) : (
                  <SectionBadge
                    section={
                      (directorClub.type as ClubType) || "conquistadores"
                    }
                    size="xs"
                  />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  {directorClub.memberCount !== undefined &&
                    `${directorClub.memberCount} miembros`}
                </p>
                <p className="text-sm text-gray-500">
                  Estado:{" "}
                  {directorClub.status === "active" ? "Activo" : "Inactivo"}
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(`/organization/clubs/${directorClub.id}`)
                }
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver detalles
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-8 text-center text-gray-500">
            No tienes un club asignado. Contacta a tu administrador de
            asociación.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Vista para administradores de Asociación
 * Vista club-céntrica: clubes como elemento principal, iglesia como label
 */
function AssociationOrganizationView() {
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const [showClubModal, setShowClubModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [clubTypeFilter, setClubTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: allClubs, isLoading } = useAccessibleClubs();
  const { data: associations } = useAssociations();
  const { data: roleAssignments } = useRoleAssignments();
  const { data: members } = useScopedMembers();
  const { data: churches } = useChurchesByAssociation(profile?.scopeId);

  // Mi asociación
  const myAssociationId = profile?.scopeId;
  const myAssociation = associations?.find((a) => a.id === myAssociationId);

  // Clubes de mi asociación
  const myClubs = useMemo(() => {
    if (!allClubs || !myAssociationId) return [];
    return allClubs.filter((c) => c.associationId === myAssociationId);
  }, [allClubs, myAssociationId]);

  // Miembros por club
  const membersByClub = useMemo(() => {
    if (!members) return {};
    const result: Record<string, number> = {};
    members.forEach((m) => {
      result[m.clubId] = (result[m.clubId] || 0) + 1;
    });
    return result;
  }, [members]);

  // Mapa de iglesias por id
  const churchMap = useMemo(() => {
    if (!churches) return {};
    const map: Record<string, (typeof churches)[0]> = {};
    churches.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [churches]);

  // Filtrar clubes por búsqueda, tipo y estado
  const filteredClubs = useMemo(() => {
    let result = myClubs;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          churchMap[c.churchId || ""]?.name?.toLowerCase().includes(term),
      );
    }

    if (clubTypeFilter !== "all") {
      result = result.filter((c) =>
        getActiveSections(c).includes(clubTypeFilter as any),
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((c) => c.status === statusFilter);
    }

    return result;
  }, [myClubs, searchTerm, clubTypeFilter, statusFilter, churchMap]);

  // Stats
  const activeClubs = myClubs.filter((c) => c.status === "active").length;
  const totalMembers = Object.values(membersByClub).reduce((a, b) => a + b, 0);

  // Cobertura de dirección por sección: cuántas secciones ACTIVAS del total
  // tienen director asignado. Es más útil que "Con Director" porque hoy un
  // club puede tener directores de sección sin director general.
  const sectionsCoverage = useMemo(() => {
    let totalActiveSections = 0;
    let withDirector = 0;
    myClubs.forEach((c) => {
      const active = getActiveSections(c);
      totalActiveSections += active.length;
      active.forEach((sec) => {
        const hasDir = roleAssignments?.some(
          (r) =>
            r.role === "club_director" &&
            r.scopeId === c.id &&
            r.section === sec &&
            r.status === "active",
        );
        // También cuenta el director general del club como cobertura de la
        // sección (director general dirige todas las secciones activas).
        const hasGeneral = roleAssignments?.some(
          (r) =>
            r.role === "club_director" &&
            r.scopeId === c.id &&
            !r.section &&
            r.status === "active",
        );
        if (hasDir || hasGeneral) withDirector++;
      });
    });
    return { total: totalActiveSections, withDirector };
  }, [myClubs, roleAssignments]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <InlineLoader size="lg" label="Cargando organización…" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            Organización
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            {myAssociation?.name || "Mi Asociación"} — {myClubs.length} clubes
          </p>
        </div>
        <Button
          onClick={() => setShowClubModal(true)}
          className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
        >
          <Plus className="w-4 h-4 mr-1" />
          Nuevo Club
        </Button>
      </div>

      {/* Stats rápidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-blue-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {myClubs.length}
                </p>
                <p className="text-sm text-gray-500">Total Clubes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-green-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {activeClubs}
                </p>
                <p className="text-sm text-gray-500">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-emerald-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {totalMembers}
                </p>
                <p className="text-sm text-gray-500">Miembros</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-purple-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {sectionsCoverage.withDirector}
                  <span className="text-lg text-gray-400 font-medium">
                    /{sectionsCoverage.total}
                  </span>
                </p>
                <p className="text-sm text-gray-500">Secciones con director</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Barra de búsqueda y filtros */}
      <Card className="border-0 shadow-lg">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar club o iglesia..."
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              />
            </div>

            {/* Filtro por tipo */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={clubTypeFilter}
                onChange={(e) => setClubTypeFilter(e.target.value)}
                className="border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              >
                <option value="all">Todos los tipos</option>
                <option value="aventureros">Aventureros</option>
                <option value="conquistadores">Conquistadores</option>
                <option value="guias_mayores">Guías Mayores</option>
              </select>
            </div>

            {/* Filtro por estado */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>

          {/* Contador de resultados */}
          {(searchTerm ||
            clubTypeFilter !== "all" ||
            statusFilter !== "all") && (
            <p className="text-sm text-gray-500 mt-3">
              {filteredClubs.length} clubes encontrados
            </p>
          )}
        </CardContent>
      </Card>

      {/* Lista de clubes */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Clubes ({filteredClubs.length})
          </h2>
        </div>

        {filteredClubs.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-8 text-center text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Building2 className="w-8 h-8 text-gray-500" />
              </div>
              <p className="font-medium">
                {searchTerm ||
                clubTypeFilter !== "all" ||
                statusFilter !== "all"
                  ? "No se encontraron clubes con esos filtros"
                  : "No hay clubes registrados"}
              </p>
              {!searchTerm &&
                clubTypeFilter === "all" &&
                statusFilter === "all" && (
                  <Button
                    className="mt-4 shadow-lg shadow-primary-500/30"
                    size="sm"
                    onClick={() => setShowClubModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Crear Primer Club
                  </Button>
                )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClubs.map((club) => {
              const activeSections = getActiveSections(club);
              const generalDirector = roleAssignments?.find(
                (r) =>
                  r.role === "club_director" &&
                  r.scopeId === club.id &&
                  !r.section &&
                  r.status === "active",
              );
              const sectionDirectors: Record<string, string | null> = {};
              activeSections.forEach((sec) => {
                const d = roleAssignments?.find(
                  (r) =>
                    r.role === "club_director" &&
                    r.scopeId === club.id &&
                    r.section === sec &&
                    r.status === "active",
                );
                sectionDirectors[sec] = d?.userName ?? null;
              });
              const memberCount = membersByClub[club.id] || 0;
              const church = churchMap[club.churchId || ""];

              return (
                <div
                  key={club.id}
                  onClick={() => navigate(`/organization/clubs/${club.id}`)}
                  className="cursor-pointer"
                >
                  <Card className="hover:shadow-lg hover:border-primary-300 transition-all duration-300 h-full border-0 shadow-md">
                    <CardContent className="pt-4">
                      {/* Header: logo + nombre + estado */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {club.logoUrl ? (
                            <img
                              src={club.logoUrl}
                              alt={`Logo de ${club.name}`}
                              className="w-10 h-10 rounded-xl object-cover shadow-sm flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-sm shadow-blue-500/25 flex-shrink-0">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {club.name}
                            </h3>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Users className="w-3 h-3 flex-shrink-0" />
                              <span>{memberCount} miembros</span>
                              {club.status !== "active" && (
                                <Badge className="bg-gray-100 text-gray-700">
                                  Inactivo
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>

                      {/* Iglesia */}
                      {church && (
                        <p className="text-xs text-gray-500 mb-3 flex items-center gap-1 flex-wrap">
                          <Church className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{church.name}</span>
                          {church.districtName && (
                            <span className="text-gray-400">
                              · {church.districtName}
                            </span>
                          )}
                          {church.code && (
                            <span className="text-gray-400">
                              · {church.code}
                            </span>
                          )}
                        </p>
                      )}

                      {/* Directores por sección */}
                      <div className="space-y-1.5 border-t border-gray-100 pt-3">
                        {activeSections.length === 0 && (
                          <p className="text-xs text-gray-400 italic">
                            Sin secciones activas
                          </p>
                        )}
                        {activeSections.map((sec) => {
                          const directorName = sectionDirectors[sec];
                          // Un director general "cubre" secciones sin director
                          // propio: se muestra su nombre con indicador.
                          const covering =
                            !directorName && generalDirector?.userName;
                          return (
                            <div
                              key={sec}
                              className="flex items-center gap-2 text-xs"
                            >
                              <SectionBadge
                                section={sec as ClubType}
                                size="sm"
                                className="shrink-0 min-w-[92px] justify-center"
                              />
                              {directorName ? (
                                <div className="flex items-center gap-1 min-w-0">
                                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                  <span
                                    className="text-gray-800 truncate"
                                    title={directorName}
                                  >
                                    {directorName}
                                  </span>
                                </div>
                              ) : covering ? (
                                <div
                                  className="flex items-center gap-1 min-w-0"
                                  title={`Cubierta por director general: ${generalDirector?.userName}`}
                                >
                                  <Shield className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                                  <span className="text-gray-500 truncate italic">
                                    {generalDirector?.userName} (general)
                                  </span>
                                </div>
                              ) : (
                                // F12-QW15: había 94 secciones activas sin
                                // director en producción y este aviso era
                                // texto muerto. La tarjeta ya navega al club
                                // (donde vive el panel de asignación): el chip
                                // ahora lo dice para que sea accionable.
                                <div
                                  className="flex items-center gap-1"
                                  title="Esta sección no tiene director asignado. Abre el club para asignarlo."
                                >
                                  <AlertCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                                  <span className="text-orange-600 underline decoration-dotted underline-offset-2">
                                    Sin director · asignar
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal para crear club */}
      <CreateClubModal
        isOpen={showClubModal}
        onClose={() => setShowClubModal(false)}
      />
    </div>
  );
}

/**
 * Vista completa para Unión (administradores)
 * Muestra asociaciones y todos los clubes
 */
function AdminOrganizationView() {
  const navigate = useNavigate();

  // Modal state
  const [modalType, setModalType] = useState<ModalType>(null);
  const [editingEntity, setEditingEntity] = useState<
    Union | Association | Club | null
  >(null);

  // Data queries - usando el nuevo hook de scope
  const { data: clubs, isLoading: loadingClubs } = useAccessibleClubs();
  const { data: unions } = useUnions();
  const [selectedUnionId, setSelectedUnionId] = useState<string | null>(null);
  const { data: associations, isLoading: loadingAssociations } =
    useAssociationsByUnion(selectedUnionId || "");

  // Auto-select first union if available
  const activeUnion = unions?.[0];
  if (activeUnion && !selectedUnionId) {
    setSelectedUnionId(activeUnion.id);
  }

  const openModal = (type: ModalType, entity?: Union | Association | Club) => {
    setModalType(type);
    setEditingEntity(entity || null);
  };

  const closeModal = () => {
    setModalType(null);
    setEditingEntity(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organización</h1>
          <p className="text-gray-500 mt-1">
            Gestiona la estructura organizacional
          </p>
        </div>
      </div>

      {/* Unión Info */}
      {activeUnion && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">{activeUnion.name}</CardTitle>
          </CardHeader>
        </Card>
      )}

      {/* Asociaciones */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Asociaciones</h2>
          <Button size="sm" onClick={() => openModal("association")}>
            <Plus className="w-4 h-4 mr-1" />
            Nueva Asociación
          </Button>
        </div>

        {loadingAssociations ? (
          <div className="flex justify-center py-8">
            <InlineLoader label="Cargando asociaciones…" />
          </div>
        ) : associations?.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-8 text-center text-gray-500">
              No hay asociaciones registradas
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {associations?.map((assoc) => (
              <Card
                key={assoc.id}
                className="hover:shadow-lg transition-all duration-300"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{assoc.name}</h3>
                      <p className="text-sm text-gray-500">{assoc.code}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Clubes */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Clubes</h2>
        </div>

        {loadingClubs ? (
          <div className="flex justify-center py-8">
            <InlineLoader label="Cargando clubes…" />
          </div>
        ) : clubs?.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-8 text-center text-gray-500">
              No hay clubes registrados
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clubs?.map((club) => (
              <div
                key={club.id}
                className="cursor-pointer"
                onClick={() => navigate(`/organization/clubs/${club.id}`)}
              >
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {club.logoUrl ? (
                          <img
                            src={club.logoUrl}
                            alt={`Logo de ${club.name}`}
                            className="w-10 h-10 rounded-xl object-cover shadow-sm"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-sm shadow-blue-500/25">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-medium">{club.name}</h3>
                          <div className="flex gap-1 flex-wrap">
                            {getActiveSections(club).map((s) => (
                              <SectionBadge
                                key={s}
                                section={s as ClubType}
                                size="xs"
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {modalType === "association" && (
        <AssociationModal
          isOpen={true}
          onClose={closeModal}
          association={editingEntity as Association | null}
          unionId={selectedUnionId || ""}
        />
      )}
    </div>
  );
}
